import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StarlineMobile.css';

function Starline({ onBack }) {
  const navigate = useNavigate();
  const [starlineGames, setStarlineGames] = useState([]);
  const [starlineRates, setStarlineRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    fetchStarlineData();
  }, []);

  const fetchStarlineData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://backend.mainbazar777.site/api/fetch-starline-game');
      
      if (!response.ok) {
        throw new Error('Failed to fetch starline data');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStarlineGames(data.starline_games || []);
        setStarlineRates(data.starline_rates || []);
      } else {
        throw new Error(data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching starline data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour.toString().padStart(2, '0')} ${minutes} ${ampm}`;
  };

  const getMarketStatus = (game) => {
    // Check if game.status is 0 (inactive)
    if (game.status !== 1) {
      return 'Market Closed';
    }

    // Get current time in Asia/Kolkata timezone
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentHours = kolkataTime.getHours();
    const currentMinutes = kolkataTime.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    // Starline opens at 12:00 AM (midnight)
    const OPEN_TIME_IN_MINUTES = 0; // 12:00 AM / 00:00

    // Parse close time - use game.close or game.open whichever is available
    const closeTimeStr = game.close || game.open;
    if (!closeTimeStr) {
      return 'Market Closed';
    }

    const [closeHours, closeMinutes] = closeTimeStr.split(':').map(num => parseInt(num));
    const closeTimeInMinutes = closeHours * 60 + closeMinutes;

    // If within 5 minutes of or past close time - market closed
    if (currentTimeInMinutes >= closeTimeInMinutes - 5) {
      return 'Market Closed';
    }

    // Between 12:00 AM and close time - market running
    return 'Market Running';
  };

  const triggerVibration = () => {
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
  };

  const handleCardClick = (game) => {
    const status = getMarketStatus(game);

    if (status === 'Market Running') {
      triggerVibration();
      navigate(`/starline-game-selection/${game.id}`, { state: { gameName: game.game_name, closeTime: game.close || game.open } });
    } else {
      triggerVibration();
      const cardElement = document.querySelector(`[data-game-id="${game.id}"]`);
      if (cardElement) {
        cardElement.classList.add('wobble');
        setTimeout(() => {
          cardElement.classList.remove('wobble');
        }, 500);
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="sl-page">
        <header className="sl-header">
          <button className="sl-back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1>Starline</h1>
          <div style={{width: '24px'}}></div>
        </header>
        <div className="sl-loading">
          <div className="sl-spinner"></div>
          <p>Loading starline games...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sl-page">
        <header className="sl-header">
          <button className="sl-back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1>Starline</h1>
          <div style={{width: '24px'}}></div>
        </header>
        <div className="sl-error">
          <p className="sl-error-msg">{error}</p>
          <button className="sl-retry-btn" onClick={fetchStarlineData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sl-page">
      {/* Header with Back Button */}
      <header className="sl-header">
        <button className="sl-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1>Starline</h1>
        <div style={{width: '24px'}}></div>
      </header>

      {/* Game Rates Card */}
      <div className="sl-rates-card">
        <h4 className="sl-rates-title">
          Starline Game Rate
        </h4>
        {starlineRates.length > 0 ? (
          starlineRates.map((rate) => (
            <div key={rate.id} className="sl-rate-row">
              <span className="sl-rate-label">{rate.market_name}</span>
              <span className="sl-rate-value">{rate.value1}-{rate.value2}</span>
            </div>
          ))
        ) : (
          <div className="sl-rate-row">
            <span className="sl-rate-label">No rates available</span>
          </div>
        )}
      </div>

      {/* History Buttons */}
      {/* <div className="sl-history-buttons">
        <button className="sl-history-btn sl-bid-history-btn" onClick={() => navigate('/starline-bid-history')}>
          Bid History
        </button>
        <button className="sl-history-btn sl-winning-history-btn" onClick={() => navigate('/starline-winning-history')}>
          Winning History
        </button>
      </div> */}

      {/* Market Cards */}
      <div className="sl-markets-container">
        {starlineGames.length > 0 ? (
          starlineGames.map((game) => (
            <div
              key={game.id}
              data-game-id={game.id}
              className="sl-market-card"
              onClick={() => handleCardClick(game)}
            >
              <div className="sl-market-header">
                <div>
                  <h3 className="sl-market-time">{game.game_name}</h3>
                  <div className="sl-market-rating">{game.results ? game.results.toString().replace(/(\d{3})(\d)/, '$1 $2') : '*** *'}</div>
                </div>
                <div className="sl-status-section">
                  <span className={`sl-market-status ${getMarketStatus(game) === 'Market Running' ? 'sl-running' : 'sl-closed'}`}>
                    {getMarketStatus(game)}
                  </span>
                  <button
                    className="sl-play-btn"
                    disabled={getMarketStatus(game) !== 'Market Running'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (getMarketStatus(game) === 'Market Running') {
                        navigate(`/starline-game-selection/${game.id}`, { state: { gameName: game.game_name, closeTime: game.close || game.open } });
                      }
                    }}
                  >
                    {getMarketStatus(game) === 'Market Running' ? (
                      <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="24" fill="#22c55e"/>
                        <path d="M19 14L33 24L19 34V14Z" fill="white"/>
                      </svg>
                    ) : (
                      <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="24" fill="#ef4444"/>
                        <line x1="16" y1="16" x2="32" y2="32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="32" y1="16" x2="16" y2="32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                  <span className="sl-play-text">{getMarketStatus(game) === 'Market Running' ? 'Play Now' : 'Closed'}</span>
                </div>
              </div>
              <div className="sl-market-timing">
                {/* <div className="sl-timing-col">
                  <span className="sl-timing-label">Open Bids:</span>
                  <span className="sl-timing-value">12 00 AM</span>
                </div> */}
                <div className="sl-timing-col">
                  <span className="sl-timing-label">Close Bids:</span>
                  <span className="sl-timing-value">{formatTime(game.open)}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="sl-no-games">
            <p>No starline games available at the moment.</p>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast-notification">
          Market Closed
        </div>
      )}
    </div>
  );
}

export default Starline;
